package com.daytimegaming.ourdailyfinances.presentation.components

import androidx.compose.animation.AnimatedVisibilityScope
import androidx.compose.animation.SharedTransitionScope
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.adaptive.currentWindowAdaptiveInfo
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.daytimegaming.ourdailyfinances.presentation.util.isCompactWidth
import org.jetbrains.compose.resources.painterResource
import ourdailyfinances.shared.generated.resources.Res
import ourdailyfinances.shared.generated.resources.app_icon

@Composable
fun AdaptiveAuthLayout(
    sharedTransitionScope: SharedTransitionScope,
    animatedVisibilityScope: AnimatedVisibilityScope,
    modifier: Modifier = Modifier,
    paneContent: @Composable (paneModifier: Modifier, showLogo: Boolean) -> Unit
) {
    val adaptiveInfo = currentWindowAdaptiveInfo()
    val isWidthNotCompact = !adaptiveInfo.windowSizeClass.isCompactWidth()

    Box(
        modifier = modifier.fillMaxSize()
    ) {
        if (isWidthNotCompact) {
            Row(
                modifier = Modifier.fillMaxSize()
            ) {
                AppLogo(
                    sharedTransitionScope = sharedTransitionScope,
                    animatedVisibilityScope = animatedVisibilityScope,
                    modifier = Modifier.weight(1f).fillMaxSize(),
                )

                paneContent(
                    Modifier
                        .weight(1f)
                        .fillMaxSize()
                        .padding(horizontal = 24.dp),
                    false
                )
            }
        } else {
            Box(
                modifier = Modifier.fillMaxSize(),
            ) {
                paneContent(Modifier.align(Alignment.Center), true)
            }
        }
    }
}

@Composable
fun AppLogo(
    sharedTransitionScope: SharedTransitionScope,
    animatedVisibilityScope: AnimatedVisibilityScope,
    iconModifier: Modifier = Modifier,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        with (sharedTransitionScope) {
            Icon(
                painter = painterResource(Res.drawable.app_icon),
                contentDescription = "App Logo",
                tint = Color.Unspecified,
                modifier = iconModifier
                    .sharedElement(
                        sharedTransitionScope.rememberSharedContentState(key = "image-logo"),
                        animatedVisibilityScope = animatedVisibilityScope,
                    )
                    .size(96.dp)
            )
            Spacer(Modifier.height(24.dp))
            AppName(
                sharedTransitionScope = sharedTransitionScope,
                animatedVisibilityScope = animatedVisibilityScope,
            )
        }
    }
}

@Composable
fun AppLogoRow(
    sharedTransitionScope: SharedTransitionScope,
    animatedVisibilityScope: AnimatedVisibilityScope,
    iconModifier: Modifier = Modifier,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(24.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        with (sharedTransitionScope) {
            Icon(
                painter = painterResource(Res.drawable.app_icon),
                contentDescription = "App Logo",
                tint = Color.Unspecified,
                modifier = iconModifier
                    .sharedElement(
                        sharedTransitionScope.rememberSharedContentState(key = "image-logo"),
                        animatedVisibilityScope = animatedVisibilityScope,
                    )
                    .size(48.dp)
            )

            AppName(
                sharedTransitionScope = sharedTransitionScope,
                animatedVisibilityScope = animatedVisibilityScope,
            )
        }
    }
}

@Composable
fun AppName(
    sharedTransitionScope: SharedTransitionScope,
    animatedVisibilityScope: AnimatedVisibilityScope,
    style: TextStyle = MaterialTheme.typography.displaySmall.copy(
        fontWeight = FontWeight.Bold,
        letterSpacing = 1.sp
    ),
) {
    with (sharedTransitionScope) {
        Text(
            text = "Our Daily Finances",
            style = style,
            color = MaterialTheme.colorScheme.onBackground,
            textAlign = TextAlign.Center,
            modifier = Modifier.sharedElement(
                sharedTransitionScope.rememberSharedContentState(key = "text-app-name"),
                animatedVisibilityScope = animatedVisibilityScope,
            )
        )
    }
}